import React, { useCallback, useEffect, useState } from 'react';
import { FaCheck, FaCheckCircle, FaExclamationTriangle, FaSpinner, FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import AdminHeader from '../../components/admin/AdminHeader';
import { stationClaimService } from '../../services/stationClaimService';
import { ClaimStatus, StationClaim } from '../../types';
import { formatCNPJ, formatCPFCNPJ, formatDateTime } from '../../utils/formatters';

const TABS: { status: ClaimStatus; label: string }[] = [
  { status: 'PENDENTE', label: 'Pendentes' },
  { status: 'APROVADO', label: 'Aprovadas' },
  { status: 'REJEITADO', label: 'Recusadas' },
];

const AdminClaims: React.FC = () => {
  const [status, setStatus] = useState<ClaimStatus>('PENDENTE');
  const [claims, setClaims] = useState<StationClaim[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await stationClaimService.getByStatus(status);
      setClaims(response.data || []);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao carregar solicitações');
    } finally {
      setIsLoading(false);
    }
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  const approve = async (claim: StationClaim) => {
    if (!window.confirm(`Aprovar ${claim.usuarioNome} como responsável por ${claim.postoNome}?`)) return;
    setBusyId(claim.id);
    try {
      await stationClaimService.approve(claim.id);
      toast.success('Solicitação aprovada');
      load();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao aprovar');
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (claim: StationClaim) => {
    const motivo = window.prompt('Motivo da recusa (o solicitante verá este texto):');
    if (motivo === null) return;
    setBusyId(claim.id);
    try {
      await stationClaimService.reject(claim.id, motivo.trim() || undefined);
      toast.success('Solicitação recusada');
      load();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao recusar');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#0f1419] pb-24 lg:pb-0">
      <AdminHeader />

      <main className="max-w-6xl mx-auto px-4 py-8">
        <h1 className="text-2xl md:text-3xl font-bold text-white">Solicitações de postos</h1>
        <p className="text-white/60 text-sm mt-1 mb-6">
          Donos de posto pedindo para assumir um posto existente ou cadastrando um novo. Ao aprovar, o posto fica ativo e
          o solicitante passa a publicar os preços.
        </p>

        <div className="flex gap-2 mb-6">
          {TABS.map((tab) => (
            <button
              key={tab.status}
              onClick={() => setStatus(tab.status)}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                status === tab.status ? 'bg-white/15 text-white' : 'text-white/60 hover:text-white hover:bg-white/5'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className="flex justify-center py-20 text-[#089453]">
            <FaSpinner className="animate-spin" size={32} />
          </div>
        ) : claims.length === 0 ? (
          <div className="text-center py-16 rounded-2xl bg-[#1a2332] border border-white/10 text-white/60">
            Nenhuma solicitação aqui.
          </div>
        ) : (
          <div className="grid gap-3">
            {claims.map((claim) => (
              <article key={claim.id} className="p-5 rounded-xl bg-[#1a2332] border border-white/10">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="space-y-1">
                    <p className="text-white font-semibold">
                      {claim.postoNome}
                      <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full bg-white/10 text-white/70 align-middle">
                        {claim.novoPosto ? 'Novo cadastro' : 'Posto existente'}
                      </span>
                    </p>
                    <p className="text-sm text-white/60">
                      {[claim.postoCnpj && `CNPJ ${formatCNPJ(claim.postoCnpj)}`, claim.postoCidade && `${claim.postoCidade}/${claim.postoEstado}`]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                    <p className="text-sm text-white/80">
                      {claim.usuarioNome} · {claim.usuarioEmail}
                      {claim.usuarioCpfCnpj && ` · ${formatCPFCNPJ(claim.usuarioCpfCnpj)}`}
                    </p>
                    <p
                      className={`text-xs flex items-center gap-1.5 ${
                        claim.documentoConfere ? 'text-[#84cc34]' : 'text-yellow-300'
                      }`}
                    >
                      {claim.documentoConfere ? <FaCheckCircle /> : <FaExclamationTriangle />}
                      {claim.documentoConfere
                        ? 'Documento do solicitante igual ao CNPJ do posto'
                        : 'Documento do solicitante diferente do CNPJ do posto — confira antes de aprovar'}
                    </p>
                    <p className="text-xs text-white/40">
                      Enviado em {formatDateTime(claim.createdAt)}
                      {claim.decididoEm && ` · decidido em ${formatDateTime(claim.decididoEm)}`}
                    </p>
                    {claim.motivoRejeicao && <p className="text-sm text-red-300/90">Motivo: {claim.motivoRejeicao}</p>}
                  </div>

                  {claim.status === 'PENDENTE' && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => reject(claim)}
                        disabled={busyId === claim.id}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg border border-red-500/40 text-red-300 hover:bg-red-500/10 disabled:opacity-60"
                      >
                        <FaTimes /> Recusar
                      </button>
                      <button
                        onClick={() => approve(claim)}
                        disabled={busyId === claim.id}
                        className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-br from-[#089453] to-[#84cc34] text-[#0f1419] font-semibold disabled:opacity-60"
                      >
                        {busyId === claim.id ? <FaSpinner className="animate-spin" /> : <FaCheck />} Aprovar
                      </button>
                    </div>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminClaims;
